// Client error report — what a crash screen shows, logs and lets someone copy,
// so the next "Something went wrong" can be diagnosed instead of guessed at.
//
// WHY. QA hit the app-level crash screen after Calculate on a payroll run
// (DEF-0mvnmww1e003) and nothing on the screen or in any log said what threw,
// where, or which request had just answered. The screen only said "An
// unexpected error has occurred".
//
// WHAT IT CARRIES. A reference, a timestamp, a STRUCTURED error (name, code,
// and, for a native error of a KNOWN shape, a fixed safe text — see safeMessage), the stack
// frames, the route as a TEMPLATE where a path segment is a credential, the
// build marker, and the LAST API request: method, masked path, status and the
// response's error `code`.
//
// WHAT IT NEVER CARRIES. Request or response bodies (they hold personal data and
// pay figures), query strings (they can hold emails, names, tokens), headers,
// cookies or tokens — including capability tokens IN THE PATH (/sign/:token,
// /ack/:token, /invite/:token, /verify/:code, the matching API calls). Paths
// and URLs quoted in messages and stacks get the same masking.
//
// WHERE IT GOES. The browser console (`[client-error]`), and the screen. There
// is no telemetry backend in this app, and this module adds no new one.

export interface LastRequest {
  method: string;
  /** Path only: no origin, no query string, no fragment. */
  path: string;
  /** HTTP status; 0 when no answer came back (network failure, timeout, abort). */
  status: number;
  /** The response's machine error code (`code` or `error.code`), when it sent one. */
  code?: string;
  at: string;
}

export interface ClientErrorReport {
  ref: string;
  at: string;
  /** The error's constructor name: TypeError, Error, AxiosError, … */
  name: string;
  /** A short machine code carried by the error (`code`), when it has one. */
  code?: string;
  /** "<name>: <text>" — sanitised and bounded, text withheld for non-engine errors (safeMessage). */
  message: string;
  stack?: string;
  componentStack?: string;
  route: string;
  build?: string;
  lastRequest: LastRequest | null;
}

const MAX_STACK = 4000;
const CODE_SHAPE = /^[A-Za-z0-9_.:-]{1,64}$/;

let lastRequest: LastRequest | null = null;

// Routes and API calls whose path segment IS a credential (a capability link:
// whoever holds the URL may act). Found by grepping router.tsx and every
// `${token}` / `${code}` URL in src: the template replaces the segment.
// Each template is a sequence of STATIC parts (alternatives with "|") ending in
// one :param. `anchored` = must start at the first path segment.
type CredentialTemplate = { parts: string[]; param: string; anchored: boolean };
const T = (p: string, param: string, anchored = false): CredentialTemplate => ({ parts: p.split('/'), param, anchored });
const CREDENTIAL_TEMPLATES: CredentialTemplate[] = [
  // API calls (longest first, so a generic rule below never pre-empts them)
  T('api/v1/credit/public/sign', 'token'),
  T('api/v1/credit/public/verify', 'code'),
  T('api/v1/work-acknowledgements', 'token'),
  T('api/v1/invitations/validate|accept', 'token'),
  T('api/v1/auth/validate-invite', 'token'),
  T('api/auth/invites', 'token'),
  T('api/auth/company-codes', 'code'),
  // app routes (router.tsx)
  T('sign', 'token', true), //        guarantor e-sign link
  T('ack', 'token', true), //         work acknowledgement link
  T('verify', 'code', true), //       document verification code
  T('ai-approval', 'id', true), //    MCP approval link
  T('invite', 'token'), //            invitation link (under any parent, e.g. /workspace)
];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// An opaque segment: 16+ chars of hex or base64url that is not a UUID (UUIDs
// are record ids — a run, an employee — and are what makes a report useful).
const OPAQUE = /^[A-Za-z0-9_-]{16,}={0,2}$/;
const looksOpaque = (seg: string) => !UUID.test(seg) && OPAQUE.test(seg) && /\d/.test(seg) && /[A-Za-z]/.test(seg);

// DEF-0mvnvp0yj003: /SIGN/<t>, /%73ign/<t>, /%2573ign/<t>, /sign%2F<t> and
// //sign//<t>/ all reach the same page (or are equally a credential) and must
// be recognised. DEF-0mvnzoaif001: but an encoded slash INSIDE the credential
// (/ack/prefix%2FSecret — the router's token is "prefix/Secret") must not
// become a new boundary that escapes the redaction. So:
//   1. The path is split on its REAL slashes into raw segments. Each raw
//      segment is percent-decoded (at most MAX_DECODE times, a malformed escape
//      leaving it as is) ONLY to compare static parts; a decoded "/" splits it
//      into PIECES for that comparison, each piece remembering its raw segment.
//   2. Empty pieces (duplicate/trailing slashes) are dropped. A dot segment
//      ("." removed, ".." pops) is only a WHOLE raw segment that decodes to
//      dots — the router never normalises inside one segment, so dots that
//      appear only after decoding an encoded slash (/sign/abc%2F..%2F..%2FTail,
//      HO-0mvo7k57y001) are literal parts of that segment, never a pop that
//      erases the token's head and the route prefix. Static parts are compared
//      case-insensitively.
//   2b. Credential segments are found on the pieces BEFORE dot normalisation
//      too: a raw segment that fills a :param there is masked whatever the
//      normalised path later looks like (more redacted, never less).
//   3. When a template matches, it is output as its lowercase template and the
//      WHOLE raw segment that holds the :param is consumed — every piece of it,
//      whatever it decodes to. A static part encoded together with the token
//      (/sign%2F<t>) is therefore recognised AND the token is still dropped;
//      where the two goals overlap, more is redacted, never less.
//   4. Other pieces are output decoded, with the :opaque rule applied; a piece
//      that still holds an escape after the bounded decode is masked.
const MAX_DECODE = 2;

type Piece = { text: string; raw: number };

function decodeBounded(seg: string): string {
  let out = seg;
  for (let i = 0; i < MAX_DECODE && /%[0-9A-Fa-f]{2}/.test(out); i++) {
    try {
      out = decodeURIComponent(out);
    } catch {
      break;
    }
  }
  return out;
}

function pieces(path: string, normalise = true): Piece[] {
  const out: Piece[] = [];
  path.split('/').forEach((rawSeg, raw) => {
    const parts = decodeBounded(rawSeg).split('/');
    const whole = parts.length === 1 ? parts[0] : null; // the raw segment did not split
    if (normalise && whole === '.') return;
    if (normalise && whole === '..') { out.pop(); return; }
    for (const text of parts) {
      if (text !== '') out.push({ text, raw });
    }
  });
  return out;
}

/** Raw segments that fill a credential :param, matched on the un-normalised pieces. */
function credentialRaws(path: string): Set<number> {
  const ps = pieces(path, false);
  const out = new Set<number>();
  for (let i = 0; i < ps.length; i++) {
    const m = matchAt(ps, i);
    if (m) out.add(ps[m.paramAt].raw);
  }
  return out;
}

/** The template matching at piece i, with the index of the piece that fills its :param. */
function matchAt(ps: Piece[], i: number): { tpl: CredentialTemplate; statics: string[]; paramAt: number } | null {
  for (const tpl of CREDENTIAL_TEMPLATES) {
    if (tpl.anchored && i !== 0) continue;
    if (i + tpl.parts.length >= ps.length) continue;
    const statics: string[] = [];
    let ok = true;
    for (let k = 0; k < tpl.parts.length; k++) {
      const got = ps[i + k].text.toLowerCase();
      if (!tpl.parts[k].split('|').includes(got)) { ok = false; break; }
      statics.push(got);
    }
    if (ok) return { tpl, statics, paramAt: i + tpl.parts.length };
  }
  return null;
}

const maskPiece = (seg: string) => (looksOpaque(seg) || /^eyJ/i.test(seg) || /%[0-9A-Fa-f]{2}/.test(seg) ? ':opaque' : seg);

/** Mask a URL PATH: credential routes become their template, opaque segments `:opaque`. */
export function maskPath(path: string): string {
  const ps = pieces(path);
  const secret = credentialRaws(path);
  const out: string[] = [];
  let i = 0;
  while (i < ps.length) {
    const m = matchAt(ps, i);
    if (!m) {
      if (secret.has(ps[i].raw)) {
        // A credential found before normalisation: masked, and consumed whole.
        out.push(':opaque');
        const raw = ps[i].raw;
        while (i < ps.length && ps[i].raw === raw) i += 1;
        continue;
      }
      out.push(maskPiece(ps[i].text));
      i += 1;
      continue;
    }
    out.push(...m.statics, `:${m.tpl.param}`);
    // Consume the whole raw segment that holds the credential — every piece of it.
    const credRaw = ps[m.paramAt].raw;
    i = m.paramAt;
    while (i < ps.length && ps[i].raw === credRaw) i += 1;
  }
  return '/' + out.join('/');
}

/** Strip origin, query string and fragment, then mask: the template is all that is kept. */
export function pathOnly(url: string): string {
  let pathname: string;
  try {
    // A path that starts with "//" is a path here, not a protocol-relative URL.
    const u = /^[a-z][a-z0-9+.-]*:/i.test(url) ? url : String(url).replace(/^\/{2,}/, '/');
    pathname = new URL(u, 'http://local.invalid').pathname;
  } catch {
    pathname = String(url).split(/[?#]/)[0];
  }
  return redact(maskPath(pathname));
}

/** Mask every URL or absolute path quoted inside free text (messages, stacks). */
function maskUrlsInText(text: string): string {
  return text
    // drop query strings and fragments wherever they appear
    .replace(/[?#][^\s"'`)<>]*/g, '')
    // full URLs → origin kept (it is a build host), path masked
    .replace(/\b(https?:\/\/[^\s/"'`)<>]+)(\/[^\s"'`)<>:]*)?/g, (_m, origin: string, p?: string) => origin + (p ? maskPath(p) : ''))
    // bare absolute paths with 2+ segments
    .replace(/(^|[\s"'`(])(\/+[A-Za-z0-9._~%-]+(?:\/+[A-Za-z0-9._~%:=-]+)+\/*)/g, (_m, pre: string, p: string) => pre + maskPath(p));
}

/** Remove anything token-, secret-, email- or amount-shaped from free text. */
export function redact(text: string): string {
  return text
    // JWTs (access/refresh/id tokens)
    .replace(/eyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]*/g, '[redacted-token]')
    // Authorization headers quoted in a message
    .replace(/\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi, '$1 [redacted]')
    // key=value / "key": "value" pairs for secrets
    .replace(
      // (?<!\/:) — not our own "/:token" path placeholder followed by ":line:col"
      /(?<!\/:)\b(access_token|refresh_token|id_token|token|csrf|xsrf|password|passwd|secret|api_key|apikey|authorization|cookie|session)(["']?\s*[:=]\s*["']?)[^\s"'&,;}]+/gi,
      '$1$2[redacted]',
    )
    // email addresses
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[redacted-email]');
}

/** Free text made safe to show: URLs masked, secrets/emails redacted, amounts hidden. */
export function sanitizeText(text: string): string {
  return redact(maskUrlsInText(text))
    // money-like numbers: 4+ digits, with or without separators/decimals
    .replace(/\b\d{1,3}(?:[,\s]\d{3})+(?:\.\d+)?\b|\b\d{4,}(?:\.\d+)?\b/g, '[number]');
}

// Native (engine) error messages describe code — but many also QUOTE THEIR
// INPUT: JSON.parse says `Unexpected token 'J', "John Kamau 1,200,000" is not
// valid JSON`, new URL() and Intl quote the value they were given
// (DEF-0mvnvp0yh001). Sanitising that text is not enough: no pattern can spot
// a name. So a native message is never shown as written. It is matched against
// the known message SHAPES below and replaced by that shape's FIXED safe text.
// The only thing that may be carried over is a token that comes from CODE — a
// property or variable name — and only when it is identifier-shaped. A quoted
// VALUE is never kept. A message that matches no shape is withheld, exactly
// like the text of every app-, library- or server-built error: the name, the
// code, the stack frames and the last request are enough to find it.
const ENGINE_ERRORS = new Set(['TypeError', 'ReferenceError', 'RangeError', 'SyntaxError', 'URIError', 'EvalError']);
const WITHHELD = 'message withheld (it can contain personal data)';

// A name quoted by a native message is NOT necessarily code: `row[userName]`
// makes V8 say "(reading 'JohnKamau')", and `obj[key]()` names the key.
// Identifier-shaped is therefore not enough — a name is kept only if it is in
// this short, reviewed set of member and global names that code uses
// everywhere; any other name is masked as "…".
//
// What is in it, and why:
//  - built-in methods/properties of arrays, strings, numbers, promises,
//    objects, functions and JSON — the bulk of real "reading 'map'" crashes;
//  - DOM / React / axios members (current, props, target, response, headers…);
//  - generic container words the app's API mapping reads (data, items, rows,
//    results, lines, employees, error, message, status, code, id, type);
//  - `name`, `value`, `label`, `key`: field LABELS, never a person's data in
//    themselves (the value read from them is never printed) and very common
//    in "reading 'name'" crashes, so worth keeping;
//  - a few globals for ReferenceErrors (window, document, process…).
// Deliberately NOT in it: anything domain- or person-specific (email, phone,
// salary, gross, net, firstName…) — a field name can say what kind of data a
// screen handled, and that is left to the stack frames to locate.
export const SAFE_MEMBER_NAMES: ReadonlySet<string> = new Set([
  // Array
  'map', 'filter', 'forEach', 'reduce', 'find', 'findIndex', 'some', 'every', 'includes', 'indexOf',
  'push', 'pop', 'shift', 'unshift', 'slice', 'splice', 'concat', 'join', 'sort', 'reverse', 'flat',
  'flatMap', 'entries', 'keys', 'values', 'at', 'length', 'fill',
  // String / Number
  'split', 'trim', 'toLowerCase', 'toUpperCase', 'replace', 'replaceAll', 'startsWith', 'endsWith',
  'substring', 'padStart', 'padEnd', 'localeCompare', 'match', 'toFixed', 'toString', 'toLocaleString',
  'valueOf', 'charAt',
  // Promise / Function / Object / JSON
  'then', 'catch', 'finally', 'call', 'apply', 'bind', 'prototype', 'constructor', 'hasOwnProperty',
  'parse', 'stringify', 'assign',
  // DOM / React / axios
  'current', 'props', 'state', 'setState', 'children', 'ref', 'style', 'classList', 'target',
  'addEventListener', 'removeEventListener', 'querySelector', 'getAttribute', 'focus', 'scrollIntoView',
  'response', 'request', 'headers', 'config', 'get', 'post', 'put', 'delete', 'getItem', 'setItem',
  // generic container / envelope words
  'data', 'items', 'rows', 'results', 'lines', 'list', 'employees', 'error', 'errors', 'message',
  'status', 'code', 'detail', 'id', 'type', 'name', 'value', 'label', 'key', 'run', 'totals',
  // globals (ReferenceError)
  'window', 'document', 'navigator', 'process', 'global', 'globalThis', 'require', 'module', 'exports',
  'React', 'fetch', 'localStorage', 'sessionStorage', 'structuredClone', 'ResizeObserver',
  'IntersectionObserver',
]);

const IDENT = /^[A-Za-z_$][\w$]{0,40}$/;
/** A code expression like `rows.map`: dotted identifiers only. */
const IDENT_CHAIN = /^[A-Za-z_$][\w$]{0,40}(?:\.[A-Za-z_$][\w$]{0,40}){0,4}$/;
const MASK = '…';
/** A single name, kept only if identifier-shaped AND in the safe set. */
const ident = (v: string | undefined) => (v && IDENT.test(v) && SAFE_MEMBER_NAMES.has(v) ? v : null);
/** A dotted chain: safe segments kept, unsafe ones masked; null when nothing safe is left. */
const chain = (v: string | undefined) => {
  if (!v || v.length > 80 || !IDENT_CHAIN.test(v)) return null;
  const parts = v.split('.').map((p) => (SAFE_MEMBER_NAMES.has(p) ? p : MASK));
  return parts.every((p) => p === MASK) && parts.length === 1 ? null : parts.join('.');
};
/** "(reading 'p')": the property, or "…" when it is not in the safe set. */
const prop = (v: string | undefined) => (ident(v) ?? MASK);

/** One known shape → its fixed safe text (without the "Name: " prefix). */
type Shape = { names?: string[]; re: RegExp; safe: (m: RegExpMatchArray) => string };

/** Exported for the docs and tests: the complete safe-message table, in match order. */
export const NATIVE_MESSAGE_SHAPES: Shape[] = [
  // a lazily loaded chunk did not load (often right after a deploy)
  { re: /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading (?:CSS )?chunk \S+ failed/i,
    safe: () => 'chunk load failed' },
  // JSON.parse — V8 "… is not valid JSON" / "… in JSON at position n", Firefox "JSON.parse: …", Safari "JSON Parse error: …"
  { names: ['SyntaxError'], re: /JSON/, safe: () => 'JSON.parse failed' },
  { names: ['TypeError'], re: /circular structure to JSON|cyclic object value|JSON\.stringify cannot serialize cyclic/i,
    safe: () => 'JSON.stringify failed (circular structure)' },
  // property access on undefined/null — V8, Firefox, Safari
  { names: ['TypeError'], re: /^Cannot read propert(?:y|ies) of (undefined|null)(?: \(reading '([^']*)'\))?/,
    safe: (m) => (m[2] !== undefined ? `Cannot read properties of ${m[1]} (reading '${prop(m[2])}')` : `Cannot read properties of ${m[1]}`) },
  { names: ['TypeError'], re: /^Cannot set propert(?:y|ies) of (undefined|null)(?: \(setting '([^']*)'\))?/,
    safe: (m) => (m[2] !== undefined ? `Cannot set properties of ${m[1]} (setting '${prop(m[2])}')` : `Cannot set properties of ${m[1]}`) },
  { names: ['TypeError'], re: /^can't access property "([^"]*)", \S+ is (undefined|null)/,
    safe: (m) => `Cannot read properties of ${m[2]} (reading '${prop(m[1])}')` },
  { names: ['TypeError'], re: /^(undefined|null) is not an object/, safe: (m) => `Cannot read properties of ${m[1]}` },
  // calling / iterating / constructing the wrong thing — the expression is code; keep it only if it is a plain identifier chain
  { names: ['TypeError'], re: /^(.+?) is not a function/, safe: (m) => `${chain(m[1]) ?? 'a value'} is not a function` },
  { names: ['TypeError'], re: /^(.+?) is not iterable/, safe: (m) => `${chain(m[1]) ?? 'a value'} is not iterable` },
  { names: ['TypeError'], re: /^(.+?) is not a constructor/, safe: (m) => `${chain(m[1]) ?? 'a value'} is not a constructor` },
  { names: ['TypeError'], re: /^Assignment to constant variable/, safe: () => 'Assignment to constant variable' },
  { names: ['TypeError'], re: /^Cannot convert a Symbol value to a string/, safe: () => 'Cannot convert a Symbol value to a string' },
  // URL constructor — the message can quote the URL
  { names: ['TypeError'], re: /Invalid URL|Failed to construct 'URL'|is not a valid URL|URL constructor/i, safe: () => 'invalid URL' },
  // network failure surfaced as a TypeError by fetch
  { names: ['TypeError'], re: /^(?:Failed to fetch|NetworkError when attempting to fetch resource|Load failed|Network request failed)/,
    safe: () => 'network request failed' },
  // undeclared / temporal-dead-zone variables — the name is code
  { names: ['ReferenceError'], re: /^(\S+) is not defined/, safe: (m) => (ident(m[1]) ? `${m[1]} is not defined` : 'a variable is not defined') },
  { names: ['ReferenceError'], re: /^Can't find variable: (\S+)/, safe: (m) => (ident(m[1]) ? `${m[1]} is not defined` : 'a variable is not defined') },
  { names: ['ReferenceError'], re: /^Cannot access '([^']*)' before initialization/,
    safe: (m) => (ident(m[1]) ? `Cannot access '${m[1]}' before initialization` : 'a variable was used before initialization') },
  // RangeError: every one is "an argument was out of range" and many quote it (Intl currency/locale/option values)
  { names: ['RangeError'], re: /Maximum call stack size exceeded|too much recursion/i, safe: () => 'maximum call stack size exceeded' },
  { names: ['RangeError'], re: /Invalid time value|Invalid Date|date value is not finite/i, safe: () => 'invalid date or time value' },
  { names: ['RangeError'], re: /Invalid array length/i, safe: () => 'invalid array length' },
  { names: ['RangeError'], re: /digits argument|precision|toFixed|toPrecision|toExponential/i, safe: () => 'invalid number of digits' },
  { names: ['RangeError'], re: /[\s\S]*/, safe: () => 'invalid argument' },
  { names: ['URIError'], re: /[\s\S]*/, safe: () => 'URI malformed' },
];

/** The report's message: "<Name>: <fixed safe text>" for a known native shape, else withheld. */
export function safeMessage(name: string, raw: string): string {
  for (const shape of NATIVE_MESSAGE_SHAPES) {
    if (shape.names && !shape.names.includes(name)) continue;
    if (!shape.names && !ENGINE_ERRORS.has(name) && name !== 'Error' && name !== 'ChunkLoadError') continue;
    const m = raw.match(shape.re);
    if (m) return `${name}: ${shape.safe(m)}`;
  }
  return `${name}: ${WITHHELD}`;
}

// A stack's first line(s) repeat the message, so only real FRAME lines are
// kept: V8 "    at fn (file:line:col)", and Firefox/Safari "fn@file:line:col".
// The Firefox form must point at a script location, so a message line that
// merely contains an "@" (an email) is never mistaken for a frame.
//
// V8 writes "<Name>: <message>" first, and the message can span SEVERAL lines —
// one of which may itself look like a frame ("    at Secret Person (a.js:1:1)").
// So the complete message is removed before any frame is accepted:
//  1. when the stack starts with exactly "<name>: <message>" (or "<message>"
//     alone, or the error was renamed after its stack was captured), that whole
//     prefix is dropped;
//  2. whatever the prefix, any line that contains a line of the message is
//     dropped too, so a message the prefix check missed still never survives.
const V8_FRAME = /^\s+at\s/;
const AT_FRAME = /^[\w$.<>/\-[\]* ]*@(?:https?:\/\/|blob:|webpack|file:|\[native code\]|<anonymous>)\S*$/;

function stripMessagePrefix(stack: string, name: string, message: string): string {
  if (!message) return stack;
  const candidates = [`${name}: ${message}`, message];
  const first = stack.split('\n')[0];
  const colon = first.indexOf(': ');
  if (colon > 0) candidates.push(`${first.slice(0, colon)}: ${message}`); // renamed after capture
  for (const c of candidates) {
    if (stack.startsWith(c)) return stack.slice(c.length);
  }
  return stack;
}

/** Stack frames only (file/line/column), sanitised and capped. */
function safeStack(stack: string, name = 'Error', message = ''): string {
  const messageLines = message
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length >= 4);
  const frames = stripMessagePrefix(stack, name, message)
    .split('\n')
    .filter((l) => (V8_FRAME.test(l) || AT_FRAME.test(l)) && !messageLines.some((m) => l.includes(m)));
  return sanitizeText(frames.join('\n')).slice(0, MAX_STACK);
}

/** Pull only a short machine error code out of an error body — never anything else. */
export function errorCodeFrom(body: unknown): string | undefined {
  let data = body;
  if (typeof data === 'string') {
    if (data.length > 20000) return undefined;
    try {
      data = JSON.parse(data);
    } catch {
      return undefined;
    }
  }
  if (!data || typeof data !== 'object') return undefined;
  const d = data as { code?: unknown; error?: unknown };
  const nested = d.error && typeof d.error === 'object' ? (d.error as { code?: unknown }).code : undefined;
  for (const c of [d.code, nested]) {
    if (typeof c === 'string' && CODE_SHAPE.test(c)) return c;
  }
  return undefined;
}

let seq = 0;
let currentSeq = 0;

/**
 * Record one finished API request. Only method, masked path, status and error
 * code are kept. Returns the request's sequence number, for enrichCode.
 */
export function recordRequest(r: { method?: string; url: string; status: number; body?: unknown }): number {
  seq += 1;
  currentSeq = seq;
  lastRequest = {
    method: (r.method || 'GET').toUpperCase(),
    path: pathOnly(r.url),
    status: r.status,
    code: r.status >= 400 && r.body !== undefined ? errorCodeFrom(r.body) : undefined,
    at: new Date().toISOString(),
  };
  return seq;
}

/**
 * Add the error code read from a body that arrived LATER (fetch). Applied only
 * while that request is still the latest one: a slow body for request A must
 * never relabel a newer request B.
 */
export function enrichCode(requestSeq: number, body: unknown): void {
  if (requestSeq !== currentSeq || !lastRequest || lastRequest.status < 400) return;
  const code = errorCodeFrom(body);
  if (code) lastRequest = { ...lastRequest, code };
}

export const getLastRequest = (): LastRequest | null => lastRequest;

/** Test seam. */
export const __resetLastRequest = () => {
  lastRequest = null;
  currentSeq = 0;
};

let installed = false;

/**
 * Watch every XHR (axios) and fetch call in the page and remember the last one.
 * Installed once from main.tsx. It reads an error body only to pick out its
 * `code`, and only for a 4xx/5xx answer.
 */
export function installLastRequestRecorder(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const XHR = window.XMLHttpRequest;
  if (XHR?.prototype) {
    const open = XHR.prototype.open;
    const send = XHR.prototype.send;
    XHR.prototype.open = function patchedOpen(this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
      (this as unknown as { __kpReq?: { method: string; url: string } }).__kpReq = { method, url: String(url) };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (open as any).call(this, method, url, ...rest);
    } as typeof XHR.prototype.open;
    XHR.prototype.send = function patchedSend(this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
      const req = (this as unknown as { __kpReq?: { method: string; url: string } }).__kpReq;
      if (req) {
        this.addEventListener('loadend', () => {
          let errBody: unknown;
          if (this.status >= 400) {
            try {
              errBody = this.responseType === '' || this.responseType === 'text' ? this.responseText : this.response;
            } catch {
              errBody = undefined;
            }
          }
          recordRequest({ method: req.method, url: req.url, status: this.status, body: errBody });
        });
      }
      return send.call(this, body as XMLHttpRequestBodyInit | null | undefined);
    };
  }

  const origFetch = window.fetch?.bind(window);
  if (origFetch) {
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const method = init?.method ?? (typeof input === 'object' && 'method' in input ? input.method : 'GET');
      try {
        const res = await origFetch(input, init);
        // Recorded the moment the answer arrives; the code is filled in from a
        // CLONE of the body later, only if nothing newer was recorded meanwhile.
        // The caller's response is never read or delayed.
        const n = recordRequest({ method, url, status: res.status });
        if (res.status >= 400) {
          try {
            res.clone().text().then((txt) => enrichCode(n, txt), () => undefined);
          } catch {
            /* body already used or not clonable — the status is enough */
          }
        }
        return res;
      } catch (e) {
        recordRequest({ method, url, status: 0 });
        throw e;
      }
    };
  }
}

function newRef(at: Date): string {
  const rand = Math.random().toString(36).slice(2, 7).toUpperCase().padEnd(5, '0');
  const stamp = at.toISOString().replace(/[-:TZ.]/g, '').slice(2, 12); // YYMMDDhhmm
  return `KP-${stamp}-${rand}`;
}

/** Build the report for an error caught by a boundary. */
export function buildErrorReport(
  error: unknown,
  opts: { route?: string; componentStack?: string | null } = {},
): ClientErrorReport {
  const now = new Date();
  const err = error as { name?: unknown; message?: unknown; stack?: unknown; code?: unknown } | null | undefined;
  const name =
    typeof err?.name === 'string' && /^[A-Za-z][A-Za-z0-9_$]{0,40}$/.test(err.name)
      ? err.name
      : typeof error === 'string'
        ? 'Error'
        : (err && typeof err === 'object' && (err as object).constructor?.name) || 'Error';
  const raw = typeof err?.message === 'string' ? err.message : typeof error === 'string' ? error : '';
  const code = typeof err?.code === 'string' && CODE_SHAPE.test(err.code) ? err.code : undefined;
  const route = opts.route ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  return {
    ref: newRef(now),
    at: now.toISOString(),
    name,
    code,
    message: safeMessage(name, raw),
    stack: typeof err?.stack === 'string' ? safeStack(err.stack, name, raw) || undefined : undefined,
    componentStack: opts.componentStack ? sanitizeText(opts.componentStack).slice(0, MAX_STACK) : undefined,
    route: pathOnly(route),
    build: typeof window !== 'undefined' ? (window as unknown as { __KP_BUILD__?: string }).__KP_BUILD__ : undefined,
    lastRequest,
  };
}

/** The plain-text form used by "Copy details" and the console log. */
export function formatErrorReport(r: ClientErrorReport): string {
  const lr = r.lastRequest;
  const lines = [
    `Reference: ${r.ref}`,
    `Time: ${r.at}`,
    `Route: ${r.route}`,
    r.build ? `Build: ${r.build}` : null,
    `Error: ${r.message}${r.code ? ` [${r.code}]` : ''}`,
    lr
      ? `Last API request: ${lr.method} ${lr.path} → ${lr.status === 0 ? 'no response' : lr.status}${lr.code ? ` (${lr.code})` : ''} at ${lr.at}`
      : 'Last API request: none recorded',
    r.stack ? `\nStack:\n${r.stack}` : null,
    r.componentStack ? `\nComponent stack:${r.componentStack}` : null,
  ];
  return lines.filter((l) => l !== null).join('\n');
}

/** Log the report. The console is the app's only error sink; no data leaves the browser. */
export function logErrorReport(r: ClientErrorReport): void {
  // eslint-disable-next-line no-console
  console.error(`[client-error] ${r.ref}\n${formatErrorReport(r)}`);
}
